import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import TERMS_SECTIONS, { TERMS_GLANCE } from "./termsContent";
import PRIVACY_SECTIONS, { PRIVACY_GLANCE } from "./privacyContent";
import { Glance, Ph } from "./legalBlocks";

import "./styles.css";

export const SHOW_DRAFT_NOTES = true;

export const LEGAL_ROUTES = {
  terms: "/terms",
  privacy: "/privacy",
};

const DOCS = {
  terms: {
    key: "terms",
    title: "Terms & Conditions",
    tab: "Terms & Conditions",
    standfirst:
      "The agreement between you and Sayaty Enterprise when you create an account, place an order, hold a wallet balance or post a review. Read section 4 carefully — it explains what Sayagie is responsible for and what your supplier is responsible for.",
    sections: TERMS_SECTIONS,
    glance: TERMS_GLANCE,
  },
  privacy: {
    key: "privacy",
    title: "Privacy Policy",
    tab: "Privacy Policy",
    standfirst:
      "How Sayagie collects, uses, shares and protects personal data when you use the Sayagie mobile app or website to order gas, fuel and related products from suppliers near you.",
    sections: PRIVACY_SECTIONS,
    glance: PRIVACY_GLANCE,
  },
};

const META = [
  ["Version", "1.0 — draft"],
  ["Effective", <Ph key="date">[date]</Ph>],
  ["Governing law", "Ghana"],
  ["Currency", "GHS (₵)"],
  ["Regulator", "NPA · DPC"],
];

const LegalDocument = ({ doc = "terms" }) => {
  const navigate = useNavigate();
  const active = DOCS[doc] || DOCS.terms;

  const sections = useMemo(
    () => active.sections.filter((section) => SHOW_DRAFT_NOTES || !section.internal),
    [active]
  );

  const [current, setCurrent] = useState(sections[0]?.id);
  const articleRef = useRef(null);

  useEffect(() => {
    setCurrent(sections[0]?.id);
    window.scrollTo(0, 0);
  }, [sections]);

  useEffect(() => {
    const nodes = sections
      .map((section) => document.getElementById(section.id))
      .filter(Boolean);

    if (!nodes.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];

        if (visible) setCurrent(visible.target.id);
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
    );

    nodes.forEach((node) => observer.observe(node));

    return () => observer.disconnect();
  }, [sections]);

  const goTo = (key) => navigate(LEGAL_ROUTES[key]);

  return (
    <div className="lg-page">
      <div className="lg-bar">
        <div className="lg-bar-in">
          <div className="lg-switch" role="tablist" aria-label="Legal documents">
            {Object.values(DOCS).map((entry) => (
              <button
                key={entry.key}
                type="button"
                role="tab"
                aria-selected={entry.key === active.key}
                onClick={() => goTo(entry.key)}
              >
                {entry.tab}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="lg-shell">
        <header className="lg-mast">
          <p className="lg-eyebrow">Sayaty Enterprise · Ghana</p>
          <h1>{active.title}</h1>
          <p className="lg-standfirst">{active.standfirst}</p>

          <dl className="lg-meta">
            {META.map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        </header>

        {SHOW_DRAFT_NOTES && (
          <div className="lg-notice">
            <h2>Draft — not yet legal advice</h2>
            <p>
              This is a working draft written from the shipped product behaviour, not a
              lawyer’s opinion. Have a Ghanaian-qualified lawyer review it before you
              publish. Every <Ph>[bracketed item]</Ph> is a placeholder you must fill,
              and they are listed in full at the end of each document.
            </p>
            <p>
              <strong>One behavioural mismatch to resolve first.</strong> The app’s{" "}
              <em>Delete Account</em> control calls the account <em>deactivation</em>{" "}
              endpoints, not deletion. Clause 13 of the Privacy Policy is written to
              describe what the product actually does. Do not replace it with a general
              erasure promise until the backend truly erases.
            </p>
          </div>
        )}

        <Glance items={active.glance} />

        <div className="lg-cols">
          <nav className="lg-rail" aria-label="Contents">
            <h2>Contents</h2>
            <ol>
              {sections.map((section) => (
                <li key={section.id}>
                  <a
                    href={`#${section.id}`}
                    className={section.id === current ? "on" : undefined}
                  >
                    <i>{section.n}</i>
                    <span>{section.title}</span>
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <article ref={articleRef}>
            {sections.map((section) => (
              <section key={section.id} id={section.id}>
                <h2>
                  <i>{section.n}</i>
                  {section.title}
                </h2>
                {section.body}
                {SHOW_DRAFT_NOTES && section.internalAfter}
              </section>
            ))}
          </article>
        </div>

        <footer className="lg-foot">
          <p>
            Sayagie is operated by <Ph>[registered company name]</Ph>, trading as Sayaty
            Enterprise. Prices in Ghana Cedis. Suppliers of petroleum products are
            licensed by the National Petroleum Authority.
          </p>
        </footer>
      </div>
    </div>
  );
};

export default LegalDocument;
